import React, { useEffect, useRef } from "react";
import { Button, Form, Badge } from "react-bootstrap";
import BlockyMediumText from "components/custom-texts/blocky-medium-text";
import { ConsoleMessage } from "hooks/useConsoleCapture";

interface ConsoleLogsProps {
  consoleLogs: ConsoleMessage[];
  autoScrollLogs: boolean;
  onClearLogs: () => void;
  onAutoScrollChange: (enabled: boolean) => void;
}

const ConsoleLogs: React.FC<ConsoleLogsProps> = ({
  consoleLogs,
  autoScrollLogs,
  onClearLogs,
  onAutoScrollChange,
}) => {
  const logsEndRef = useRef<HTMLDivElement>(null);

  const getLogLevelColor = (type: ConsoleMessage['type']): string => {
    switch (type) {
      case 'error': return '#dc3545';
      case 'warn': return '#ffc107';
      case 'info': return '#17a2b8';
      default: return '#212529';
    }
  };

  const getLogLevelBadge = (type: ConsoleMessage['type']): string => {
    switch (type) {
      case 'error': return 'danger';
      case 'warn': return 'warning';
      case 'info': return 'info';
      default: return 'primary';
    }
  };

  useEffect(() => {
    if (autoScrollLogs && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [consoleLogs, autoScrollLogs]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button variant="danger" size="sm" onClick={onClearLogs}>
            Clear Logs
          </Button>
          <Form.Check 
            type="switch"
            id="auto-scroll-switch"
            label="Auto-scroll"
            checked={autoScrollLogs}
            onChange={(e) => onAutoScrollChange(e.target.checked)}
          />
        </div>
        <BlockyMediumText style={{ color: "#666", fontSize: "12px" }}>
          Showing logs from when debug page opened
        </BlockyMediumText>
      </div>
      
      <div style={{
        border: "1px solid #dee2e6",
        borderRadius: "8px",
        backgroundColor: "#000",
        maxHeight: "400px",
        overflowY: "scroll",
        fontFamily: "monospace",
        fontSize: "13px",
        padding: "10px"
      }}>
        {consoleLogs.length === 0 ? (
          <div style={{ color: "#6c757d", textAlign: "center", padding: "20px" }}>
            No console logs yet. Try interacting with the app or run console.log() in browser console.
          </div>
        ) : (
          consoleLogs.map((log) => (
            <div 
              key={log.id} 
              style={{ 
                marginBottom: "8px", 
                padding: "6px 8px",
                backgroundColor: "rgba(255,255,255,0.05)",
                borderRadius: "4px",
                borderLeft: `4px solid ${getLogLevelColor(log.type)}`
              }}
            >
              <div style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: "8px", 
                marginBottom: "4px" 
              }}>
                <Badge bg={getLogLevelBadge(log.type)} style={{ fontSize: "10px" }}>
                  {log.type.toUpperCase()}
                </Badge>
                <span style={{ 
                  color: "#6c757d", 
                  fontSize: "11px" 
                }}>
                  {new Date(log.timestamp).toLocaleTimeString()}.{new Date(log.timestamp).getMilliseconds().toString().padStart(3, '0')}
                </span>
              </div>
              <div style={{ 
                color: getLogLevelColor(log.type),
                whiteSpace: "pre-wrap",
                wordBreak: "break-word"
              }}>
                {log.message}
              </div>
              {log.stack && (
                <div style={{ 
                  color: "#6c757d",
                  fontSize: "11px",
                  whiteSpace: "pre-wrap",
                  marginTop: "4px",
                  paddingTop: "4px",
                  borderTop: "1px solid rgba(255,255,255,0.1)"
                }}>
                  {log.stack}
                </div>
              )}
            </div>
          ))
        )}
        <div ref={logsEndRef} />
      </div>
    </>
  );
};

export default ConsoleLogs;